import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { stakeholdersCatPage_Query } from "@app/queries/stakeholdersCatPage_Query.graphql";
import type { stakeholdersCatPage_AssignMutation } from "@app/queries/stakeholdersCatPage_AssignMutation.graphql";

// Cost counterparties can number in the hundreds on a real tenant, so the list
// pages server-side (search spans the whole set, not just the page).
export const PAGE_SIZE = 50;

export const stakeholdersCatQuery = graphql`
  query stakeholdersCatPage_Query(
    $perspectiveId: ID
    $from: String
    $search: String
    $uncategorized: Boolean
    $limit: Int
    $offset: Int
  ) {
    costStakeholders(
      perspectiveId: $perspectiveId
      from: $from
      search: $search
      uncategorized: $uncategorized
      limit: $limit
      offset: $offset
    ) {
      totalCount
      stakeholders {
        id
        name
        total
        category {
          id
          name
          color
        }
      }
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

const assignMutation = graphql`
  mutation stakeholdersCatPage_AssignMutation(
    $input: StakeholdersCategoryAssignInputObject!
  ) {
    stakeholdersCategoryAssign(input: $input) {
      stakeholders {
        id
      }
      errors {
        message
      }
    }
  }
`;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

type Payload = stakeholdersCatPage_Query["response"]["costStakeholders"];

export default function StakeholdersCatPage({
  queryRef,
  uncategorizedOnly = false,
}: {
  queryRef: PreloadedQuery<stakeholdersCatPage_Query>;
  uncategorizedOnly?: boolean;
}) {
  const preloaded = usePreloadedQuery(stakeholdersCatQuery, queryRef);

  const [list, setList] = useState<Payload>(preloaded.costStakeholders);
  useEffect(() => setList(preloaded.costStakeholders), [preloaded]);

  const categories = preloaded.categories;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [categoryId, setCategoryId] = useState<string>("");
  const [onlyUncategorized, setOnlyUncategorized] = useState(uncategorizedOnly);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const rows = list.stakeholders;
  const totalCount = list.totalCount;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const vars = () => ({
    perspectiveId: getPerspectiveId() ?? undefined,
    from: dateRangeFromISO(getDateRange()) ?? undefined,
    search: debouncedSearch.trim() || null,
    uncategorized: onlyUncategorized,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const fetchPage = () => {
    setIsFetching(true);
    fetchQuery<stakeholdersCatPage_Query>(environment, stakeholdersCatQuery, vars())
      .toPromise()
      .then((fresh) => fresh && setList(fresh.costStakeholders))
      .catch((error) => window.alert(error.message))
      .finally(() => setIsFetching(false));
  };

  // Refetch on search / page change (skip first render — the loader has page 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page, onlyUncategorized]);

  const navigate = useNavigate();
  useEffect(() => {
    navigate({
      to: "/stakeholders-cat",
      search: onlyUncategorized ? { uncategorized: true } : {},
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyUncategorized]);

  const allSelected = rows.length > 0 && selected.size === rows.length;

  const toggle = (id: string) =>
    setSelected((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  // One row, same mutation as the bulk action: a single write path means an
  // inline edit and a batch edit can never diverge in what they persist.
  const assignOne = (stakeholderId: string, nextCategoryId: string) => {
    if (!nextCategoryId) return;
    commitMutation<stakeholdersCatPage_AssignMutation>(environment, {
      mutation: assignMutation,
      variables: { input: { stakeholderIds: [stakeholderId], categoryId: nextCategoryId } },
      onCompleted: (_response, errors) => {
        if (errors?.length) {
          window.alert(errors[0].message);
          return;
        }
        fetchPage();
      },
      onError: (error) => window.alert(error.message),
    });
  };

  const assign = () => {
    if (!categoryId || selected.size === 0) return;
    commitMutation<stakeholdersCatPage_AssignMutation>(environment, {
      mutation: assignMutation,
      variables: { input: { stakeholderIds: [...selected], categoryId } },
      onCompleted: (_response, errors) => {
        if (errors?.length) {
          window.alert(errors[0].message);
          return;
        }
        setSelected(new Set());
        fetchPage();
      },
      onError: (error) => window.alert(error.message),
    });
  };

  const uncategorized = useMemo(
    () => rows.filter((r) => !r.category).length,
    [rows]
  );

  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;

  const pager =
    totalCount > PAGE_SIZE ? (
      <div className="flex items-center justify-between text-sm text-gray-500 py-2">
        <span>
          Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          </span>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-bold">Stakeholders by cost</h1>
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search by name…"
          className="min-w-56 rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={onlyUncategorized}
            onChange={(e) => {
              setOnlyUncategorized(e.target.checked);
              setSelected(new Set());
              setPage(0);
            }}
          />
          Uncategorized only
        </label>
        <span className="text-sm text-gray-500">
          {onlyUncategorized
            ? `${totalCount.toLocaleString()} counterpart${
                totalCount === 1 ? "y has" : "ies have"
              } spend but no category`
            : `${uncategorized} of ${totalCount.toLocaleString()} cost counterparties uncategorized`}
        </span>
        {isFetching && (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            role="status"
            aria-label="Loading"
          />
        )}
      </div>

      {pager}

      <div
        className={`bg-white border border-gray-200 rounded-lg overflow-hidden transition-opacity duration-200 ${
          isFetching ? "opacity-50" : ""
        }`}
      >
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="w-8 px-3 py-2 text-left">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th className="px-3 py-2 text-left font-medium">Counterparty</th>
              <th className="px-3 py-2 text-right font-medium">Net total</th>
              <th className="px-3 py-2 text-left font-medium">Category</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="p-4 text-gray-400">
                  {onlyUncategorized
                    ? "Nothing left — every cost counterparty has a category."
                    : "No cost counterparties."}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                className={`hover:bg-gray-50 ${
                  selected.has(row.id) ? "bg-amber-50" : ""
                }`}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggle(row.id)}
                  />
                </td>
                <td className="px-3 py-2 text-gray-900">{row.name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                  {money(row.total)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {row.category && (
                      <span
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: row.category.color }}
                      />
                    )}
                    <select
                      aria-label={`Category for ${row.name}`}
                      value={row.category?.id ?? ""}
                      onChange={(e) => assignOne(row.id, e.target.value)}
                      className={`w-full max-w-56 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-gray-200 focus:border-gray-300 focus:outline-none ${
                        row.category ? "text-gray-900" : "text-gray-400"
                      }`}
                    >
                      <option value="">— uncategorized —</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pager}

      {selected.size > 0 && (
        <div className="sticky bottom-0 mt-4 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3">
          <span className="text-sm font-medium">
            {selected.size} selected — assign to
          </span>
          <select
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
          >
            <option value="">Select a category…</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={assign}
            disabled={!categoryId}
            className="px-3 py-1.5 rounded bg-gray-900 text-white text-sm disabled:opacity-40"
          >
            Assign
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-sm text-gray-500 hover:text-gray-900 underline"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
